import {Component, OnInit, HostListener} from '@angular/core';
import { Router } from '@angular/router';
import * as Rx from 'rxjs';
import { switchMap, mergeMap, toArray, tap } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators} from '@angular/forms';
import { MagliveApiClient } from 'src/lib/api-client/maglive-api-client';
import {
  Live,
  LiveThumbnail,
  ArchiveCreateRequest,
  ArchiveAddResponse
} from "src/lib/entity/live";
import { catchError, concatMap, map } from 'rxjs/operators';
import { ToastrService, ActiveToast } from 'ngx-toastr';
import { environment } from 'src/env/environment';
import { FirestoreAccess } from "src/lib/firestore-access";
import { containNgWordValidator } from 'src/app/parts/validation/admin/ngWordValidator.directive';
import { DeliverableMagazine } from 'src/lib/entity/magazine';
import { getUniqueStr } from 'src/lib/util';
@Component({
  selector: 'admin-archive-create',
  templateUrl: './archive-create.component.html',
  styleUrls: [ '../../../../pages/admin/startbootstrap-sb-admin-gh-pages/src/scss/styles.scss', './archive-create.component.scss']
})
export class ArchiveCreateComponent implements OnInit {

  /**
   * アップロード上限 18GB
   * ECSのエフェメラルストレージのデフォルト上限が20GBであり、-2GB余裕を持たせている。
   */
  private MAX_FILE_SIZE = 19327352832;
  
  /**
   * 分割アップロードの各チャンクの最大ファイルサイズ 500MB
   */
  private FILE_CHUNK_SIZE = 50_000_000;

  /**
   * マルチパートアップロードの固定処理数。
   * プログレスバーの割合計算で使用する。処理単位は以下の３つとする
   * 1. マルチパートアップロード開始
   * 2. 署名付きURLの発行
   * 3. マルチパートアップロード終了
   * 
   * 署名付きURLによる実際のアップロード処理数は含めない。
   * 後続のチャンクファイル分割処理時に、算出したチャンク数を合算する。
   */
  private UPLOAD_PROCESS_COUNT = 3;

  /**
   * オブジェクトアップロードの最大並列数 15個
   */
  private MAX_UPLOAD_PARALLELS_COUNT = 15
  
  /**
   * クリック無効秒数 3秒
   */
  private CLICK_INVALID_SECONDS = 3000

  defaultThumbnailUrl: string;

  // フォーム関係
  formGroup: FormGroup;
  private selectedThumbnailFile: File;
  invalidThumbnailSize: boolean;
  isWaiting: boolean;
  private allNgWordList: string[];

  // セレクトボックス関係
  deliverableMagazineList$: Rx.Observable<DeliverableMagazine[] | "NOT_FOUND">;
  deliverableMagazineList: DeliverableMagazine[];
  selectMagazine: DeliverableMagazine;

  // アーカイブライブの情報
  private archive: Live
  private archiveId: string;

  /**
   * マルチパートアップロードの実行ID
   * マルチアップロード開始時に発行される。以下の時に使用する
   * 1. 署名付きURL発行時
   * 2, マルチアップロード終了時
   * https://docs.aws.amazon.com/ja_jp/AmazonS3/latest/userguide/mpuoverview.html
   */
  private uploadId: string;
  // 選択中のアップロードファイル
  selectedArchiveFile: File;
  // アップロード先のS3オブジェクトのパス
  private objectPath: string;
  // アップロードファイルの拡張子
  private objectFileType: string;
  // マルチパートアップロードのパーツ数
  private chunkCount = 0
  // 全てのアップロード処理数
  private totalProcess = 0;
  // 進行中のアップロード処理数
  private processCount = 0;
  // アップロード進行状況のパーセンテージ
  private processPercent = 0;
  // トースター表示用のオブジェクト
  private toastObj: ActiveToast<any>

  private subscription: Rx.Subscription;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private firestoreAccess: FirestoreAccess,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (environment.name === "local") {
      this.defaultThumbnailUrl = 'https://live.stg.mag2.com/assets/images/thumbnail.png';
    } else {
      this.defaultThumbnailUrl = environment.baseURL.magliveWeb + 'assets/images/thumbnail.png';
    }

    this.formGroup = this.fb.group({
      userId: [null, Validators.required],
      magazineId: [null, [Validators.required,Validators.min(0)]],
      title: [null, [
        Validators.required,
        Validators.maxLength(40)
      ]],
      description: [null, Validators.maxLength(140)],
      viewerSegment: ['ALL',Validators.pattern("ALL|SUBSCRIBER")],
      isScreenRotated: [false, Validators.pattern("true|false")],
      isViewerCountEnabled: [true, Validators.pattern("true|false")],
      thumbnailPath: [null],
      thumbnailURL: [this.defaultThumbnailUrl],
      isLimitedTimeRelease: [false, Validators.pattern("true|false")],
      archiveDays: [null, Validators.min(1)],
      creatorComment: [null, Validators.maxLength(140)],
      archiveFile: [null, Validators.required],
    });

    this.getNgWord();
    this.getDeliverableMagazines();
  }

  get userId() { return this.formGroup.get('userId'); }
  get magazineId() { return this.formGroup.get('magazineId'); }
  get title() { return this.formGroup.get('title'); }
  get description() { return this.formGroup.get('description'); }
  get viewerSegment() { return this.formGroup.get('viewerSegment'); }
  get isScreenRotated() { return this.formGroup.get('isScreenRotated'); }
  get isViewerCountEnabled() { return this.formGroup.get('isViewerCountEnabled'); }
  get thumbnailPath() { return this.formGroup.get('thumbnailPath'); }
  get thumbnailURL() { return this.formGroup.get('thumbnailURL'); }
  get creatorComment() { return this.formGroup.get('creatorComment'); }
  get isLimitedTimeRelease() { return this.formGroup.get('isLimitedTimeRelease'); }
  get archiveDays() { return this.formGroup.get('archiveDays'); }
  get archiveFile() { return this.formGroup.get('archiveFile'); }

  getNgWord(magazineId?: number) {
    this.allNgWordList = [];
    MagliveApiClient.listGlobalNgWords()
    .subscribe( x => {
      if(x !== "NOT_AUTHORIZED") {
        x.map(ngWord => {
          this.allNgWordList.push(ngWord.word);
        })
      }
    })

    if (magazineId) {
      this.firestoreAccess.getNgwords(magazineId)
      .subscribe(x => {
        x.map(ngWord=> {
          this.allNgWordList.push(ngWord.word);
        })
      })
    }

    this.title.setValidators([containNgWordValidator(this.allNgWordList),Validators.required, Validators.maxLength(40)]);
    this.description.setValidators([containNgWordValidator(this.allNgWordList), Validators.maxLength(140)]);
    this.creatorComment.setValidators([containNgWordValidator(this.allNgWordList), Validators.maxLength(140)]);
  }

  getDeliverableMagazines():void {
    this.deliverableMagazineList$ = MagliveApiClient.getMagazineList();
    this.deliverableMagazineList$.subscribe(x => {
      if (x != "NOT_FOUND") {
        this.deliverableMagazineList = x;
      }
    })
  }

  changeViewerCountCheckbox(event: any) {
    this.formGroup.patchValue({
      isViewerCountEnabled: !event.target.checked
    });
  }

  changeArchiveCheckBox(event: any) {
    if(event.target.checked) {
      this.formGroup.controls.archiveDays.enable();
      this.formGroup.patchValue({
        isLimitedTimeRelease: true,
        archiveDays: 1,
      });
      this.archiveDays.setValidators([Validators.required, Validators.min(1)]);
    } else {
      this.formGroup.controls.archiveDays.disable();
      this.formGroup.patchValue({
        isLimitedTimeRelease: false,
        archiveDays: null
      });
      this.archiveDays.setValidators([Validators.min(1)]);
    }
    this.archiveDays.updateValueAndValidity();
  }

  changeDeliverableMagazine(magazineId: number): void {
    this.deliverableMagazineList.forEach(magazine => { 
      if (magazine.magazineId == magazineId) {
        this.userId.patchValue(magazine.userId);
        this.selectMagazine = magazine;
      } 
    })
  }

  removeSelectMagazine(): void {
    this.selectMagazine = null;
    this.userId.patchValue("");
  }

  changeThumbnail(event: any): void {
    const reader = new FileReader()
    if (event.target.files.length > 0) {
      const file = event.target.files[0];

      this.selectedThumbnailFile = file;
      reader.onload = (event => {
        let image = new Image;
        image.src = reader.result as string;
        image.onload = () => {
          let factor = Math.abs(image.width / image.height - 16.0/9.0);
          const allowed = 0.015;
          this.invalidThumbnailSize = allowed <= factor;
        }
        this.formGroup.patchValue({
          thumbnailURL: reader.result
        });
      })
      reader.readAsDataURL(file);
    }
  }
  clickThumbnailButton(event: any): void {
    event.target.value = '';
  };

  removeSelectingThumbnail(): void {
    if(this.thumbnailURL.value != this.defaultThumbnailUrl) {
      this.selectedThumbnailFile = null;
      this.invalidThumbnailSize = false;
      this.formGroup.patchValue({
        thumbnailPath: null,
        thumbnailURL: this.defaultThumbnailUrl
      });
    }
  }

  clickArchiveButton(event: any): void {
    event.target.value = '';
  };

  changeArchiveFile(event: any): void {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.selectedArchiveFile = file;
      const fileSize = file.size
      if(fileSize > this.MAX_FILE_SIZE) {
        this.archiveFile.setErrors({ isMaxFileSize: true});
      }

      this.calcChunkCount(fileSize)
      this.calcAspectRatio(file)
    } 
  }

  /**
   * 選択された動画のチャンク数を計算する
   * @param fileSize 
   */
  private calcChunkCount(fileSize: number) {
    this.chunkCount = Math.ceil(fileSize / this.FILE_CHUNK_SIZE)
  }

  /**
   *  選択された動画の縦横比を算出する
   * @param file 
   */
  private calcAspectRatio(file : File) {
    const videoElement = document.createElement( "video" );
    videoElement.src = URL.createObjectURL(file);
    videoElement.onloadedmetadata = () => {
      this.formGroup.patchValue({
        isScreenRotated: videoElement.videoWidth > videoElement.videoHeight,
      });
    }
  }

  removeSelectingArchive(): void {
    if(this.selectedArchiveFile) {
      this.selectedArchiveFile = null;
      this.formGroup.patchValue({
        archiveFile: null
      });
      this.chunkCount = 0
    }
  }

  /**
   * プログレスバーの割合計算およびトースターを更新する
   * @param toast 
   */
  private updateProgressMeter(toast: ActiveToast<any>) {
    this.processCount++;
    this.processPercent =
      this.totalProcess > 0 ? Math.round((this.processCount / this.totalProcess) * 100) : 0;
    toast.toastRef.componentInstance.message = `アップロード中... ${this.processPercent}%`;
  }

  /**
   * 選択された動画をアーカイブライブとしてアップロードする
   */
  public storeArchive() {
    if(this.isWaiting) return
    this.isWaiting = true

    this.toastObj = this.toastr.info(`アップロード中... ${this.processPercent}%`, null, {
      disableTimeOut: true,
      tapToDismiss: false
    });

    const thumbnail$ = this.postThumbnail(this.selectedThumbnailFile)
    const live$ = this.postLive(thumbnail$)
    
    this.totalProcess += this.chunkCount + this.UPLOAD_PROCESS_COUNT

    const uploadObject$ = this.uploadObject(live$)
   
    this.subscription = this.convertMp4ToHls(uploadObject$).subscribe((room) => {
      if(room && room.convert_hls_status == "progress") return 
      setTimeout( () => {
        this.isWaiting = false;
        this.router.navigate(["admin/lives"], {queryParams: {filter: "upload"}})
        this.subscription.unsubscribe()
      }, this.CLICK_INVALID_SECONDS);
      this.toastr.success("アーカイブアップロードしました")
    },
    error => {
      console.error(error)
      setTimeout( () => {this.isWaiting = false;}, this.CLICK_INVALID_SECONDS);
      this.toastr.error("アーカイブアップロードに失敗しました")
    })
  }

  /**
   * サムネイル画像を登録する
   * @param file 
   * @returns
   */
  private postThumbnail = (file: File): Rx.Observable<LiveThumbnail | "NOT_AUTHORIZED"> => {
    let thumbnail$ = Rx.of<"NOT_AUTHORIZED" | LiveThumbnail>({
      thumbnailPath: this.thumbnailPath.value,
      thumbnailURL: this.thumbnailURL.value
    })

    if(this.thumbnailURL.value
      && this.thumbnailURL.value != this.defaultThumbnailUrl
      && this.selectedThumbnailFile) {
      const formData = new FormData();
      formData.append('file', file);
      thumbnail$ = MagliveApiClient.createLiveThumbnail(
        this.magazineId.value,
        formData
      ).pipe(
        map(response => {
          if(response == "NOT_AUTHORIZED") {
            return response
          }
          const thumbnailResult = response as LiveThumbnail;
          this.formGroup.patchValue({
            thumbnailPath: thumbnailResult.thumbnailPath,
            thumbnailURL: thumbnailResult.thumbnailURL
          });
          return thumbnailResult
        }),
        catchError(err => {
          return Rx.throwError(err);
        })
      )
    }
    
    return thumbnail$
  }

  /**
   * アーカイブライブを作成する
   * @param thumbnail$ 
   * @returns
   */
  private postLive = (thumbnail$: Rx.Observable<LiveThumbnail | "NOT_AUTHORIZED">): Rx.Observable<string> => {
    return thumbnail$.pipe(
      concatMap(thumbnail => {
        const thumbnailResult = thumbnail as LiveThumbnail;
        const params: ArchiveCreateRequest = {
          userId: this.userId.value,
          magazineId: this.magazineId.value,
          title: this.title.value,
          description: this.description.value,
          thumbnailPath: thumbnailResult.thumbnailPath && thumbnailResult.thumbnailURL != this.defaultThumbnailUrl ? thumbnailResult.thumbnailPath : null,
          roomConfigId: 1,
          options: {
            isViewerCountEnabled: this.isViewerCountEnabled.value,
            secondsViewWithoutAuthority: null, // APIで設定するのでnullを入れておく
            isScreenRotated: this.isScreenRotated.value,
            viewerSegment: this.viewerSegment.value,
            commentSegment: "LOGIN",
            reactionSegment: "LOGIN",
            isArchived: null, // APIで設定するのでnullを入れてる
            archiveDays: this.archiveDays.value ?? 0,
          }
        }

        return MagliveApiClient.addArchiveInfo(params).pipe(
          catchError((err) => {
            return Rx.throwError(err);
          }),
        );
      }),
      map(x => {
        const res = x as ArchiveAddResponse;
        this.archiveId = res.liveId
        return res.liveId
      }),
      tap(liveId => {
        this.firestoreAccess.getLive(liveId).subscribe(live => {
          this.archive = live
        })
      }),
      concatMap(liveId => {
        if (this.creatorComment.value) {
          const params = {
            userId: this.userId.value,
            message: this.creatorComment.value,
            device: null
          };
          return MagliveApiClient.createCreatorComment(liveId, params).pipe(
            map(_ => liveId),
            catchError((err)=> {
              return Rx.throwError(err);
            })
          );
        } 
        return Rx.of(liveId)
      }),
    )
  }

  /**
   * 動画ファイルをS3にアップロードする
   * @param liveId$ 
   * @returns 
   */
  private uploadObject = (liveId$: Rx.Observable<string>) => {
    return liveId$.pipe(
      concatMap(liveId => {
        this.objectFileType = this.selectedArchiveFile.name.split('.').pop()
        this.objectPath = getUniqueStr() + "/index." + this.objectFileType

        // 1. マルチパートアップロード開始処理
        const uploadId$ = MagliveApiClient.createMultipartUpload(
          liveId,
          this.objectPath
        ).pipe(
          map(x => x.uploadId),
          tap(_ => {
            this.updateProgressMeter(this.toastObj)
          }),
          catchError(err => {
            return Rx.throwError(err);
          })
        )

        // 2. 署名付きURLを発行する（オブジェクトをアップロードするエンドポイント）
        const urlList$ = uploadId$.pipe(
          switchMap((uploadId) => {
            this.uploadId = uploadId
            return MagliveApiClient.createPreSignedUrls(
              uploadId,
              this.objectPath,
              this.chunkCount
            ).pipe(
              tap(_ => this.updateProgressMeter(this.toastObj)),
              catchError(err => {
                return Rx.throwError(err);
              })
            )
          })
        )

        // 3. 動画ファイルを分割し、パーツ毎に署名付きURLでアップロードする
        const multipartsEtags$ = urlList$.pipe(
          switchMap(urlList => {
            let offset = 0;
            return Rx.from(urlList).pipe(
              // 並列で最大15個ずつアップロード
              mergeMap((urlItem) => {
                // 動画をチャンクファイルに分割する
                const chunkData$ = Rx.from(
                  new Promise((resolve) => {
                    let reader = new FileReader();
                    reader.onload = (e: any) => {
                      const data = new Uint8Array(e.target.result);
                      resolve(data)
                      reader.abort();
                    }
                    const slice = this.selectedArchiveFile.slice(offset, offset + this.FILE_CHUNK_SIZE, this.selectedArchiveFile.type);
                    offset += this.FILE_CHUNK_SIZE;
                    reader.readAsArrayBuffer(slice);
                    
                  }) as Promise<Uint8Array>
                ).pipe(
                  catchError(err => {
                    return Rx.throwError(err);
                  })
                )

                return chunkData$.pipe(
                  concatMap(data => {
                    // チャンクファイルをアップロードする
                    return Rx.from(
                      MagliveApiClient.uploadPreSignedUrl(urlItem.url, data)).pipe(
                        map((x) => { 
                          return {
                            partNumber: urlItem.partNumber,
                            etag: x
                          }
                        }
                      )
                    )
                  }),
                  catchError(err => {
                    return Rx.throwError(err);
                  })
                )
              }, this.MAX_UPLOAD_PARALLELS_COUNT),
              tap(_ => this.updateProgressMeter(this.toastObj)),
              toArray(),
              catchError(err => {
                return Rx.throwError(err);
              })
            )
          })
        )
          
        // 4. マルチパートアップロード完了処理
        return multipartsEtags$.pipe(
          map(etags => {
            const multipartList = []
            for (let i = 0; i < etags.length; i++) {
              multipartList.push({
                ETag: etags[i].etag,
                partNumber: etags[i].partNumber,
              });
            }
            
            return multipartList
          }),
          concatMap(multipartList => {
            return MagliveApiClient.completeMultipartUpload(
              liveId,
              this.uploadId,
              this.objectPath,
              multipartList
            )
          }),
          tap(_ => {
            this.updateProgressMeter(this.toastObj)
            this.toastr.clear(this.toastObj.toastId);
            this.processCount = 0;
            this.processPercent = 0;
            this.totalProcess = 0;
          }),
          catchError(err => {
            this.resetUploadStatus(this.archiveId)
            this.toastr.clear(this.toastObj.toastId);
            this.processCount = 0;
            this.processPercent = 0;
            this.totalProcess = 0;
            return Rx.throwError(err);
          })
        )
      })
    )
  }

  /**
   * アップロードしたS3オブジェクト（mp4）をHLS形式に変換する
   * @param objectUpload$ 
   * @returns 
   */
  private convertMp4ToHls = (objectUpload$: Rx.Observable<void>) => {
    return objectUpload$.pipe(
      tap(x => {
        this.toastObj = this.toastr.info(`変換中... `, null, {
          disableTimeOut: true,
          tapToDismiss: false
        });
      }),
      concatMap(_ => {
        return MagliveApiClient.convertArchive(this.archiveId, this.objectPath, this.objectFileType).pipe(
          map(x => x.roomId)
        )
      }),
      concatMap(roomId => this.firestoreAccess.getRoom(roomId).pipe(
        map(room => {
          if(room) {
            // 変換成功または失敗時（変換Lambdaの内部エラー）、トースターを削除する
            if(room.convert_hls_status == "success" || room.convert_hls_status == "failed") {
              this.toastr.clear(this.toastObj?.toastId);
            }
          }
          return room
        }),
      )),
      catchError(err => {
        this.toastr.clear(this.toastObj?.toastId);
        return Rx.throwError(err);
      })
    )
  }

  /**
   * ライブのアップロードステータスを初期化する
   * @param liveId 
   */
  private resetUploadStatus(liveId: string) {
    MagliveApiClient.updateArchiveUploadStatusByFetch(liveId, "INIT")
  }

  /**
   * ブラウザを閉じる前・リロード前に、アップロード中のライブがある場合に確認ダイアログを出す
   * @param e イベント
   */
  @HostListener('window:beforeunload', ['$event'])
  beforeunloadHandler(e: Event) {
    if(this.isWaiting) {
      /**
       * アップロード中の条件判定
       * 1. ライブのアップロードステータスが「アップロード中」 且つ まだライブにroomIdが紐付いていない
       * 2. ライブアップロードステータスが「完了」且つ まだライブにroomIdが紐付いていない
       */
      if((this.archive?.archiveUploadStatus === "PROGRESS" && this.archive?.roomId == null) || (this.archive?.archiveUploadStatus === "COMPLETED" && this.archive?.roomId == null)) {
        e.returnValue = true;
      }
    }
  }

  /**
   * 上記の確認ダイアログでOKとした場合、アップロード中である全ライブのアップロードステータスを初期化する
   */
  @HostListener('window:unload', ['$event'])
  unloadHandler() {
    /**
     * アップロード中の条件判定
     * 1. ライブのアップロードステータスが「アップロード中」 且つ まだライブにroomIdが紐付いていない
     * 2. ライブアップロードステータスが「完了」且つ まだライブにroomIdが紐付いていない
     */
    if((this.archive?.archiveUploadStatus === "PROGRESS" && this.archive?.roomId == null) || (this.archive?.archiveUploadStatus === "COMPLETED" && this.archive?.roomId == null)) {
      this.resetUploadStatus(this.archiveId)
    }
  }
}
